import React, { useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { InvoicesListFilter } from './InvoicesListFilter'
import { AccountingSystemDialog } from '../common/AccountingSystemDialog'
import { generateAndDownloadInvoicePdfFile, generateAndDownloadInvoiceXmlFile } from '../common/helpers'
import { SendInvoiceDialog } from '../common/SendInvoiceDialog'
import { SmartTable } from '../common/table/Table'
import { SmartTableContext } from '../common/table/TableContext'
import { SmartTableHeadCell } from '../common/table/TableHeadCell'
import { SmartTableWrapper } from '../common/table/TableWrapper'
import { SortDir } from '../common/table/types'
import axiosClient from '../service/axiosClient'
import { AccountingSystem } from '../types/AccountingSystem'
import { PageableResponse, PagingRequest } from '../types/Common'
import { formatMoney } from '../types/Currency'
import { InvoiceDTO, InvoicesFilter } from '../types/Invoice'

export const InvoicesList = () => {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const [filterInternal, setFilterInternal] = useState<InvoicesFilter>({
        from: null,
        to: null,
        unpaidOnly: false,
    })

    // Invoice the accounting-system picker is open for, null when it is closed
    const [exportInvoice, setExportInvoice] = useState<InvoiceDTO | null>(null)

    // Invoice waiting for its recipient to be confirmed, null when the dialog is closed
    const [sendInvoice, setSendInvoice] = useState<InvoiceDTO | null>(null)

    function loadDataFn(queryParams: PagingRequest) {
        console.log('queryParams', queryParams)
        const pageNumber = queryParams.pageStart || 0
        const pageSize = queryParams.pageSize || 15
        return new Promise<PageableResponse<InvoiceDTO>>(
            (resolve) => {
                axiosClient.get('invoice', {
                    params: {
                        pageSize: pageSize,
                        pageStart: pageNumber,
                        from: filterInternal.from?.toISOString().slice(0, 10),
                        to: filterInternal.to?.toISOString().slice(0, 10),
                        status: filterInternal.unpaidOnly ? 'UNPAID' : undefined,
                        sortDesc: queryParams.sortDesc,
                    }
                }).then(response => {
                    const totalElements = response.data.totalElements
                    const paginatedData = response.data.content
                    return resolve({
                        hasNextPage: true,
                        totalElements: totalElements,
                        content: paginatedData,
                    })
                }).catch(error => {
                    console.log('error', error)
                })
            }
        )
    }

    async function updateInvoiceStatus(id: number, newStatus: 'PAID' | 'UNPAID', reload: () => void) {
        try {
            await axiosClient.patch(`invoice/${id}/status`, null, {
                params: { status: newStatus }
            })
            toast.success(newStatus === 'PAID' ? t('invoice_marked_paid') : t('invoice_marked_unpaid'))
            reload()
        } catch (error) {
            toast.error(t('invoice_status_update_error'))
        }
    }

    async function deleteInvoice(id: number, reload: () => void) {
        if (confirm(t('confirm_delete_invoice'))) {
            await axiosClient.delete('invoice/' + id)
            toast.success(t('invoice_deleted_success'))
            reload()
        }
    }

    function sendPdfFile(id: string, email: string) {
        axiosClient.get('/invoice/generate/' + id).then(() => {
            axiosClient.get('/invoice/send-by-email/' + id, {params: {email}})
                .then(() => toast.success(t('invoice_sent_success')))
                .catch(() => toast.error(t('invoice_send_error')))
        })
    }


    return <>
        <InvoicesListFilter filter={filterInternal} setFilter={setFilterInternal}/>
        {/*<div className="rounded-content-body mb-4">*/}
        <SmartTableWrapper<InvoiceDTO, InvoicesFilter>
            initialPageSize={10}
            // Newest first; the header turns it to the oldest
            initialSortBy="date"
            initialSortDir={SortDir.Desc}
            filter={filterInternal}
            loadDataFn={loadDataFn}>
            <SmartTableContext.Consumer>
                {api => (
                    <SmartTable<InvoiceDTO>
                        onRowClick={(item) => {
                            // navigate('/invoices/edit/' + item.id)
                        }}
                        HeadRow={
                            <>
                                {/* Ordered by the day the invoice was issued, which is the order of its numbers */}
                                <SmartTableHeadCell sort="date">
                                    {t('invoice_number')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('recipient')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('due_date')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('status')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('total_amount')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell
                                    className="text-end">
                                    {t('action')}
                                </SmartTableHeadCell>
                            </>
                        }
                        renderRow={(item) => {
                            return (
                                <>
                                    <td>{item.invoiceId}</td>
                                    {/* A sole trader invoices under their own name and has no company */}
                                    <td>{item.customerBusinessName?.trim()
                                        || [item.customerFirstName, item.customerLastName]
                                            .filter(Boolean).join(' ')}</td>
                                    <td>{item.invoiceDueDate ? new Date(item.invoiceDueDate).toLocaleDateString('sk-SK') : '-'}</td>
                                    <td>
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                const newStatus = item.invoiceStatus === 'PAID' ? 'UNPAID' : 'PAID'
                                                updateInvoiceStatus(item.id, newStatus, () => api.load())
                                            }}
                                            title={t('click_to_change_status')}
                                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                                        >
                                            {item.invoiceStatus === 'PAID' ? (
                                                <span className="badge bg-success">{t('paid')}</span>
                                            ) : (
                                                <span className="badge bg-warning text-dark">{t('unpaid')}</span>
                                            )}
                                        </a>
                                    </td>
                                    <td>{formatMoney(item.totalWithDph ? item.totalWithDph : 0, item.currency)}</td>
                                    <td className="text-right">
                                        <div className="d-flex gap-2 justify-content-end">
                                            <a href={'#'} className={'table-action-link'} onClick={(e) => {
                                                e.preventDefault()
                                                setSendInvoice(item)
                                            }} title={t('send_to_email')}>
                                                <i className="bi bi-envelope-at primary-text-color"></i>
                                                {/*<button className="btn amani-primary amani-small">Upraviť</button>*/}
                                            </a>
                                            <a href={'#'} className={'table-action-link'} onClick={(e) => {
                                                e.preventDefault()
                                                generateAndDownloadInvoicePdfFile(item.id.toString(), item.invoiceId.toString())
                                            }} title={t('download_pdf')}>
                                                <i className="bi bi-download primary-text-color"></i>
                                            </a>
                                            <a href={'#'} className={'table-action-link'} onClick={(e) => {
                                                e.preventDefault()
                                                setExportInvoice(item)
                                            }} title={t('download_xml')}>
                                                <i className="bi bi-filetype-xml primary-text-color"></i>
                                            </a>
                                            <Link className={'table-action-link'} to={'/app/invoices/edit/' + item.id + '?invoiceNo=' + item.invoiceId}
                                                  title={t('edit')}>
                                                <i className="bi bi-pencil primary-text-color"></i>
                                                {/*<button className="btn amani-primary amani-small">Upraviť</button>*/}
                                            </Link>
                                            <a href={'#'} className={'table-action-link'} onClick={(e) => {
                                                e.preventDefault()
                                                item.id && deleteInvoice(item.id, () => api.load())
                                            }} title={t('delete')}>
                                                <i className="bi bi-trash primary-text-color"></i>
                                                {/*<button className="btn amani-primary amani-small">Upraviť</button>*/}
                                            </a>
                                        </div>
                                    </td>
                                </>
                            )
                        }}
                    />)}</SmartTableContext.Consumer>
        </SmartTableWrapper>
        <SendInvoiceDialog show={!!sendInvoice}
                           defaultEmail={sendInvoice?.customerEmail}
                           onClose={() => setSendInvoice(null)}
                           onConfirm={(email: string) => {
                               if (sendInvoice) {
                                   sendPdfFile(sendInvoice.id.toString(), email)
                               }
                           }}/>
        <AccountingSystemDialog show={!!exportInvoice}
                                onClose={() => setExportInvoice(null)}
                                onConfirm={(system: AccountingSystem) => {
                                    if (exportInvoice) {
                                        generateAndDownloadInvoiceXmlFile(
                                            exportInvoice.id.toString(),
                                            exportInvoice.invoiceId.toString(),
                                            system)
                                    }
                                }}/>
        {/*</div>*/}
    </>
}